import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import whishPayController from "../controllers/whishPayController";
import { userService } from "../services/userService";
import "../styles/WishPay.css";
import { translateLabel, useTranslationSnapshot } from "./../translations/runtime";


const formatMoney = (amount, currency = "USD") => {
  const numeric = Number(amount);

  if (!Number.isFinite(numeric)) return `${amount || "0"} ${currency}`;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "LBP" ? 0 : 2,
  }).format(numeric);
};

const getStoredToken = () => {
  const storedUser = userService.getJwt();
  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? {};

const SUCCESS_PAYMENT_STATUSES = new Set([
  "APPROVED",
  "CAPTURED",
  "COMPLETED",
  "PAID",
  "SUCCESS",
  "SUCCESSFUL",
]);

const PENDING_PAYMENT_STATUSES = new Set([
  "PENDING",
  "PROCESSING",
  "CREATED",
  "INITIATED",
]);

export default function WishPay() {
  useTranslationSnapshot();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const amount = params.get("amount") || location.state?.amount || "0";
  const collectUrl = params.get("collectUrl") || location.state?.collectUrl || "";
  const currency = params.get("currency") || location.state?.currency || "USD";
  const paymentId = params.get("paymentId") || location.state?.paymentId || "";
  const requestId = params.get("requestId") || location.state?.requestId || "";
  const mode = params.get("mode") || location.state?.mode || "withdraw";
  const returnTo =
    params.get("returnTo") || location.state?.returnTo || "/digital-wallet";
  const isTopUp = mode === "topup";
  const [syncState, setSyncState] = useState({
    error: "",
    loading: false,
    payment: null,
  });
  const [initialPaymentState, setInitialPaymentState] = useState({
    error: "",
    loading: false,
    payment: null,
  });
  const syncedPaymentStatus =
    syncState.payment?.paymentStatus ||
    initialPaymentState.payment?.paymentStatus ||
    syncState.payment?.transactionStatus ||
    initialPaymentState.payment?.transactionStatus ||
    syncState.payment?.collectStatus ||
    initialPaymentState.payment?.collectStatus ||
    "";
  const isPaymentSuccessful = SUCCESS_PAYMENT_STATUSES.has(
    String(syncedPaymentStatus || "").toUpperCase(),
  );
  const isPaymentPending =
    !isPaymentSuccessful &&
    PENDING_PAYMENT_STATUSES.has(String(syncedPaymentStatus || "").toUpperCase());
  const shouldShowWhishButton = Boolean(collectUrl) && !isPaymentSuccessful;
  const shouldShowMissingLink = !collectUrl && !isPaymentSuccessful;
  const goBackToWallet = () => {
    const walletUrl = new URL(returnTo, window.location.origin);

    if (isPaymentSuccessful) {
      walletUrl.searchParams.set("refreshWallet", "1");
      walletUrl.searchParams.set("paymentId", paymentId);
    }

    navigate(`${walletUrl.pathname}${walletUrl.search}`);
  };
  const continueToWhish = () => {
    if (!collectUrl) return;

    window.location.assign(collectUrl);
  };
  const checkPaymentStatus = async () => {
    if (!paymentId || syncState.loading) return;

    setSyncState({ error: "", loading: true, payment: null });
    window.dispatchEvent(new Event("vendor-loading:start"));

    try {
      const response = await whishPayController.syncPayment({
        Authorization: getStoredToken(),
        paymentId,
      });
      const payment = unwrapPayload(response);

      setSyncState({ error: "", loading: false, payment });
    } catch (error) {
      setSyncState({
        error:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to check payment status.",
        loading: false,
        payment: null,
      });
    } finally {
      window.dispatchEvent(new Event("vendor-loading:stop"));
    }
  };

  useEffect(() => {
    let isCurrentRequest = true;

    const loadPayment = async () => {
      if (!paymentId) return;

      setInitialPaymentState({ error: "", loading: true, payment: null });

      try {
        const response = await whishPayController.getPayment({
          Authorization: getStoredToken(),
          paymentId,
        });
        const payment = unwrapPayload(response);

        if (!isCurrentRequest) return;

        setInitialPaymentState({ error: "", loading: false, payment });
      } catch (error) {
        if (!isCurrentRequest) return;

        setInitialPaymentState({
          error:
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load payment.",
          loading: false,
          payment: null,
        });
      }
    };

    loadPayment();

    return () => {
      isCurrentRequest = false;
    };
  }, [paymentId]);

  return (
    <main className="wishpay-shell">
      <header className="wishpay-topbar">
        <Link className="wishpay-wordmark" to="/digital-wallet"> {translateLabel("txt_end_user_privacy_policy_255", "D2i2a W Noss")}<span> / Pay</span>
        </Link>
        <span className="wishpay-quiet-label">{translateLabel("txt_admin_secure_payment", "Secure payment")}</span>
      </header>

      <section className="wishpay-intro">
        <span className="wishpay-eyebrow">
          {isTopUp ? translateLabel("txt_admin_wallet_top_up", "Wallet top up") : translateLabel("txt_admin_wallet_withdrawal", "Wallet withdrawal")}
        </span>
        <h1>{isTopUp ? translateLabel("txt_admin_confirm_top_up", "Confirm top up") : translateLabel("txt_admin_confirm_withdrawal", "Confirm withdrawal")}</h1>
        <p>{translateLabel("txt_admin_review_the_amount_before_continuing_with_whish_pay", "Review the amount before continuing with Whish Pay.")}</p>
      </section>

      <section className="wishpay-layout">
        <div className="wishpay-card">
          <div className="wishpay-card-heading">
            <span className="wishpay-step">01</span>
            <div>
              <h2>{isTopUp ? translateLabel("txt_admin_top_up_request", "Top up request") : translateLabel("txt_admin_withdrawal_request", "Withdrawal request")}</h2>
              <p>{translateLabel("txt_admin_your_request_is_ready_for_payment_confirmation", "Your request is ready for payment confirmation.")}</p>
            </div>
          </div>

          <p className="wishpay-payment-amount">
            {formatMoney(amount, currency)}
          </p>
          <p className="wishpay-payment-description">
            {isTopUp
              ? translateLabel("txt_admin_funds_will_be_added_to_your_wallet_after_successful_payment_confirmation", "Funds will be added to your wallet after successful payment confirmation.")
              : translateLabel("txt_admin_funds_will_be_deducted_from_your_wallet_after_successful_payment_confirmation", "Funds will be deducted from your wallet after successful payment confirmation.")}
          </p>

          <dl className="wishpay-details">
            <div>
              <dt>{translateLabel("txt_admin_method", "Method")}</dt>
              <dd>{translateLabel("btn_whish_pay", "Whish Pay")}</dd>
            </div>
            <div>
              <dt>{translateLabel("lnk_currency", "Currency")}</dt>
              <dd>{currency}</dd>
            </div>
            {requestId && (
              <div>
                <dt>{translateLabel("txt_admin_reference", "Reference")}</dt>
                <dd>{requestId}</dd>
              </div>
            )}
            {paymentId && (
              <div>
                <dt>{translateLabel("txt_admin_payment_id", "Payment ID")}</dt>
                <dd>{paymentId}</dd>
              </div>
            )}
          </dl>

          <div className="wishpay-button-row">
            {shouldShowWhishButton && (
              <button
                className="wishpay-primary"
                type="button"
                onClick={continueToWhish}
              > {translateLabel("txt_business_continue_to_whish", "Continue to Whish")} </button>
            )}
            <button
              className="wishpay-secondary"
              type="button"
              onClick={goBackToWallet}
            >
              {isPaymentSuccessful ? translateLabel("txt_admin_return_and_refresh_wallet", "Return and refresh wallet") : translateLabel("txt_admin_back_to_wallet", "Back to wallet")}
            </button>
            <button
              className="wishpay-secondary"
              type="button"
              disabled={!paymentId || syncState.loading}
              onClick={checkPaymentStatus}
            >
              {syncState.loading
                ? translateLabel("txt_end_user_checking", "Checking...")
                : isPaymentPending
                  ? translateLabel("txt_admin_check_again", "Check again")
                  : translateLabel("txt_admin_check_payment_status", "Check payment status")}
            </button>
          </div>

          {shouldShowMissingLink && (
            <div className="wishpay-status wishpay-status-error">
              Whish payment link is not available yet.
            </div>
          )}

          {(syncedPaymentStatus || syncState.error) && (
            <div
              className={`wishpay-status ${
                syncState.error
                  ? "wishpay-status-error"
                  : isPaymentSuccessful
                    ? "wishpay-status-success"
                    : isPaymentPending
                      ? "wishpay-status-pending"
                      : ""
              }`}
            >
              {syncState.error ||
                (isPaymentSuccessful
                  ? translateLabel("txt_admin_payment_confirmed_you_can_return_to_refresh_your_wallet", "Payment confirmed. You can return to refresh your wallet.")
                  : `Payment status: ${String(syncedPaymentStatus).replace(
                      /_/g,
                      " ",
                    )}`)}
            </div>
          )}
          {initialPaymentState.loading && (
            <div className="wishpay-status">Loading payment status...</div>
          )}
          {initialPaymentState.error && !syncState.error && (
            <div className="wishpay-status wishpay-status-error">
              {initialPaymentState.error}
            </div>
          )}
        </div>

        <aside className="wishpay-card wishpay-how-it-works">
          <span className="wishpay-eyebrow">{translateLabel("txt_business_what_to_expect", "What to expect")}</span>
          <h2>{translateLabel("txt_admin_payment_flow", "Payment flow")}</h2>
          <ol>
            <li>
              <b>{translateLabel("txt_admin_review_amount", "Review amount")}</b>
              <span>{translateLabel("txt_admin_make_sure_the_wallet_amount_is_correct", "Make sure the wallet amount is correct.")}</span>
            </li>
            <li>
              <b>{translateLabel("txt_admin_pay_with_whish", "Pay with Whish")}</b>
              <span>{translateLabel("txt_admin_continue_to_whish_and_confirm_the_transaction", "Continue to Whish and confirm the transaction.")}</span>
            </li>
            <li>
              <b>{translateLabel("txt_admin_return_here", "Return here")}</b>
              <span>{translateLabel("txt_admin_your_wallet_status_can_be_refreshed_after_confirmation", "Your wallet status can be refreshed after confirmation.")}</span>
            </li>
          </ol>
        </aside>
      </section>
    </main>
  );
}
